import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ErrorService } from '../shared/services/error.service';

export interface ExternalLink {
  id: number;
  code?: string | null;
  createdBy?: string | null;
  updatedBy?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  raw?: any;
}

export interface ExternalLinkCreate {
  createdBy: string;
  updatedBy: string;
}

@Injectable({ providedIn: 'root' })
export class ExternalLinksService {
  private readonly baseUrl = environment.microservices?.order || environment.apiUrl || '/api';
  private readonly resourcePath = 'externallinks';

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {}

  private getHeaders(): HttpHeaders {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    const headerMap: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    if (token) {
      headerMap['Authorization'] = `Bearer ${token}`;
    }

    return new HttpHeaders(headerMap);
  }

  private buildUrl(path: string): string {
    const [base, baseQuery] = String(this.baseUrl).split('?');
    const normalizedBase = base.replace(/\/+$/, '');
    const normalizedPath = String(path).replace(/^\/+/, '');
    let url = `${normalizedBase}/${normalizedPath}`;
    if (baseQuery) {
      url += `?${baseQuery}`;
    }
    return url;
  }

  private extractItem(response: any): any {
    return response?.data?.data ?? response?.data ?? response?.item ?? response?.externalLink ?? response;
  }

  private mapFromDto(dto: any): ExternalLink {
    return {
      id: dto?.Id ?? dto?.id ?? 0,
      code: dto?.Code ?? dto?.code ?? null,
      createdBy: dto?.CreatedBy ?? dto?.createdBy ?? null,
      updatedBy: dto?.UpdatedBy ?? dto?.updatedBy ?? null,
      createdAt: dto?.CreatedAt ?? dto?.createdAt ?? null,
      updatedAt: dto?.UpdatedAt ?? dto?.updatedAt ?? null,
      raw: dto
    };
  }

  /**
   * POST /externallinks
   */
  create(payload: ExternalLinkCreate): Observable<ExternalLink> {
    return this.http.post<any>(this.buildUrl(this.resourcePath), payload, { headers: this.getHeaders() }).pipe(
      map(res => this.mapFromDto(this.extractItem(res))),
      tap(() => this.errorService.showSuccessMessage('Link externo criado com sucesso')),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao criar link externo');
        return throwError(() => err);
      })
    );
  }

  /**
   * POST /externallinks/validate/{code}
   */
  validateCode(code: string): Observable<any> {
    const encoded = encodeURIComponent(code);
    return this.http.post<any>(this.buildUrl(`${this.resourcePath}/validate/${encoded}`), null, { headers: this.getHeaders() }).pipe(
      map(res => res?.data?.data ?? res?.data ?? res),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao validar código do link externo');
        return throwError(() => err);
      })
    );
  }

  /**
   * DELETE /externallinks/{id}
   */
  delete(id: number): Observable<void> {
    return this.http.delete<void>(this.buildUrl(`${this.resourcePath}/${id}`), { headers: this.getHeaders() }).pipe(
      map(() => void 0),
      tap(() => this.errorService.showSuccessMessage('Link externo removido com sucesso')),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao remover link externo');
        return throwError(() => err);
      })
    );
  }
}
